import { useCallback, useMemo, useRef, useState } from 'react';

function getByPath(obj, path) {
    if (!obj || path == null) return undefined;
    if (typeof path !== 'string') return undefined;
    return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

function looksLikeIsoDate(value) {
    return typeof value === 'string' && (/^\d{4}-\d{2}-\d{2}/.test(value) || /T\d{2}:\d{2}/.test(value));
}

function compareValues(a, b) {
    const aEmpty = a == null || a === '';
    const bEmpty = b == null || b === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    if (typeof a === 'boolean' || typeof b === 'boolean') {
        return Number(Boolean(a)) - Number(Boolean(b));
    }

    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }

    const numericRe = /^-?\d+(\.\d+)?$/;
    const na = typeof a === 'string' && numericRe.test(a.trim()) ? Number(a) : NaN;
    const nb = typeof b === 'string' && numericRe.test(b.trim()) ? Number(b) : NaN;
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;

    if (looksLikeIsoDate(a) && looksLikeIsoDate(b)) {
        const da = Date.parse(a);
        const db = Date.parse(b);
        if (Number.isFinite(da) && Number.isFinite(db)) return da - db;
    }

    return String(a).localeCompare(String(b), 'es', { numeric: true, sensitivity: 'base' });
}

function sortByKey(rows, sortKey, sortDir, getters = {}) {
    if (!Array.isArray(rows) || !sortKey) return Array.isArray(rows) ? rows : [];
    const getter = getters[sortKey] || ((row) => getByPath(row, sortKey));
    const copy = [...rows];
    copy.sort((a, b) => {
        const cmp = compareValues(getter(a), getter(b));
        return sortDir === 'desc' ? -cmp : cmp;
    });
    return copy;
}

/**
 * Client-side table sorting. Click a column header to toggle asc/desc.
 *
 * @param {Array} rows
 * @param {string|{ defaultKey?: string, defaultDir?: 'asc'|'desc', getters?: Record<string, Function> }} options
 */
export function useTableSort(rows, options = {}) {
    const opts = typeof options === 'string' ? { defaultKey: options } : (options || {});
    const { defaultKey = null, defaultDir = 'asc', getters = {} } = opts;

    const [sortKey, setSortKey] = useState(defaultKey);
    const [sortDir, setSortDir] = useState(defaultDir);
    const gettersRef = useRef(getters);
    gettersRef.current = getters;

    const toggleSort = useCallback((key) => {
        if (!key) return;
        setSortKey((prev) => {
            if (prev === key) {
                setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                return prev;
            }
            setSortDir('asc');
            return key;
        });
    }, []);

    const setSort = useCallback((key, dir = 'asc') => {
        setSortKey(key);
        setSortDir(dir);
    }, []);

    const sortRows = useCallback((list) => (
        sortByKey(Array.isArray(list) ? list : [], sortKey, sortDir, gettersRef.current)
    ), [sortKey, sortDir]);

    const sorted = useMemo(() => sortRows(rows), [rows, sortRows]);

    return {
        sorted,
        sortKey,
        sortDir,
        toggleSort,
        setSort,
        sortRows,
        thProps: { sortKey, sortDir, onSort: toggleSort },
    };
}
